// src/common/pages/authorValidation/CampoEditable.tsx
import React from "react";
import { Box, TextField } from "@mui/material";
import { InputPhone } from "../../../entities/common/controls/InputPhone";
import { etiquetaDeCampo } from "./datos";
import type { CampoConfig, Idioma, OpcionDeCatalogo, Valores } from "./tipos";

/**
 * Las filas que un campo de referencia ofrece: las del catálogo, y si depende
 * de otro campo (la provincia, del país) solo las de lo elegido en ese.
 */
export function filasDeReferencia(
  campo: CampoConfig,
  valores: Valores,
  catalogos: Record<string, OpcionDeCatalogo[]>
): OpcionDeCatalogo[] {
  const padre = campo.compositeWith ? valores[campo.compositeWith] ?? "" : "";
  return (catalogos[campo.refEntity ?? ""] ?? []).filter(
    (o) => !campo.compositeWith || !padre || o.parentId === padre
  );
}

/**
 * Los valores que la lista del campo permite elegir, o `null` si el campo no
 * es una lista. Un valor guardado que no está entre ellos no se ve en la lista
 * nativa (queda en blanco), así que para el formulario es como si no hubiera.
 */
export function valoresElegibles(
  campo: CampoConfig,
  valores: Valores,
  catalogos: Record<string, OpcionDeCatalogo[]>
): string[] | null {
  if (campo.type === "Enum") return (campo.options ?? []).map((o) => o.value);
  if (campo.type === "Ref") return filasDeReferencia(campo, valores, catalogos).map((o) => o.id);
  return null;
}

export interface CampoEditableProps {
  campo: CampoConfig;
  /** Todos los valores del formulario: una provincia se filtra por el país elegido. */
  valores: Valores;
  catalogos: Record<string, OpcionDeCatalogo[]>;
  idioma: Idioma;
  opcional: boolean;
  textoOpcional: string;
  error?: string;
  /** Una ayuda debajo del campo, cuando no hay error. */
  ayuda?: string;
  onChange: (valor: string) => void;
}

/**
 * Un campo, según cómo lo describe el registro de validación. Las listas son
 * nativas: en un teléfono abren el selector del sistema.
 */
export const CampoEditable: React.FC<CampoEditableProps> = ({
  campo,
  valores,
  catalogos,
  idioma,
  opcional,
  textoOpcional,
  error,
  ayuda,
  onChange,
}) => {
  const valor = valores[campo.name] ?? "";
  const base = etiquetaDeCampo(campo, idioma);
  const etiqueta = opcional && !valor ? `${base} (${textoOpcional})` : base;
  const comunes = {
    id: `campo-${campo.name}`,
    label: etiqueta,
    fullWidth: true,
    size: "small" as const,
    sx: { mb: 2 },
    error: !!error,
    helperText: error ?? ayuda,
  };

  if (campo.phone) {
    // InputPhone (generado) se dibuja en un div de 500 px fijos y 8 px de margen
    // en línea, y su caja interna mide lo que mide su contenido: en un teléfono
    // desborda la pantalla. Se lo ajusta desde afuera, sin tocarlo: el `!important`
    // es necesario porque lo que pisa va en el atributo style.
    return (
      <Box
        sx={{
          mb: 2,
          "& > div": { width: "100% !important", margin: "0 !important" },
          "& .PhoneInput": { flex: 1, minWidth: 0 },
        }}
      >
        <InputPhone
          label={etiqueta}
          value={valor}
          error={!!error}
          helperText={error ?? ayuda}
          onChange={(v: string) => onChange(v ?? "")}
        />
      </Box>
    );
  }

  if (campo.type === "Enum") {
    return (
      <TextField
        {...comunes}
        select
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" />
        {(campo.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label[idioma] || o.label.es || o.value}
          </option>
        ))}
      </TextField>
    );
  }

  if (campo.type === "Ref") {
    const filas = filasDeReferencia(campo, valores, catalogos);
    return (
      <TextField
        {...comunes}
        select
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" />
        {filas.map((o) => (
          <option key={o.id} value={o.id}>
            {o.displayId}
          </option>
        ))}
      </TextField>
    );
  }

  if (campo.type === "Date") {
    return (
      <TextField
        {...comunes}
        type="date"
        InputLabelProps={{ shrink: true }}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <TextField
      {...comunes}
      value={valor}
      onChange={(e) => onChange(e.target.value)}
      inputProps={campo.email ? { inputMode: "email" } : undefined}
    />
  );
};
